// ============================================================
// src/shack/shack.js — Shack Virtual 3D editable
// ============================================================

import { auth } from '../firebase.js';
import { obtenerAccessToken } from '../drive.js';
import { solicitarPermisosDrive } from '../auth.js';
import { mostrarToast, mostrarConfirmacion } from '../ui.js';
import { t } from '../i18n.js';
import { getPerfilActual } from '../perfil.js';
import { abrirEncuadrador } from './encuadrador.js';
import {
  T,
  MAPA_YAESU_URL,
  CAMARA,
  IMAGE_RATIO,
  STAGE,
  MIN_W,
  MAX_W,
  MIN_TONE,
  MAX_TONE,
  PREVIEW_EXPORT,
} from './shackConfig.js';
import { cargarEstadoShack, guardarEstadoShack } from './shackPersistence.js';
import { obtenerShack, cargarCertificadosDisponibles, guardarShackPublico, subirImagenesPublicasShack, actualizarPreviewPublica, verificarShackPublicado, limpiarImagenesHuerfanas } from './shackDatos.js';
import { actualizarShackVisibleArchivo } from '../firestore.js';
import {
  round4,
  clampNum,
  surfacePoint,
  surfaceFrameAt,
  surfaceRectPoint,
  updateWarpedMeshGeometry,
} from './shackSurface.js';
import {
  defaultCalibration,
  cargarCalibracion,
  guardarCalibracionLocal,
  imprimirYGuardarCalibracion,
  rebuildGrid,
  crearGrids,
  crearHelperRect,
  actualizarHelperRect,
  ocultarHelperRect,
  crearMultiSelectRects,
  actualizarMultiSelectRects,
  mostrarGrid,
  ocultarGrid,
  crearDOMCalibrationUI,
  actualizarDOMCalibrationUI,
  crearWallPickMeshes,
  actualizarVisualCalibracion,
} from './shackCalibration.js';
import {
  getShackState,
  setListo,
  setUsuarioUid,
  selectedItem as selectedItemState,
  itemByKey as itemByKeyState,
  meshByItemKey as meshByItemKeyState,
  nextZIndex as nextZIndexState,
  ordenarPorZIndex as ordenarPorZIndexState,
  guardarDebounced as guardarDebouncedState,
  cancelarGuardadoDebounced,
} from './shackState.js';
import {
  configurarInteraccion,
  configurarHotkeys,
  crearHandlersInteraccion,
} from './shackInteraction.js';
import { crearItemsApi } from './shackItems.js';
import {
  crearCertificadoGroup as crearCertificadoGroupMod,
  crearMapaGroup as crearMapaGroupMod,
  limpiarCacheFrameTextures,
} from './shackFrames.js';
import { generarThumbnailsEnLote, limpiarCacheThumbnails } from './thumbnails.js';
import {
  crearLayoutDOM,
  crearToolbar,
  crearRenderer,
  actualizarLayoutStage,
  centrarStageSiCorresponde,
  aplicarCameraViewOffset,
  aplicarViewport,
  resetearViewport,
  configurarTooltip,
  abrirMapaZoom,
  abrirItemFullscreen,
  mostrarSpinner,
} from './shackUi.js';

const _state = getShackState();
let _itemsApi = null;

// ── Estado de preview automática ─────────────────────────────
let _previewTimer = null;
let _callsignPublicado = null;
let _previewVersion = null;

// ── Zoom / Pan del canvas ────────────────────────────────────
const ZOOM_MAX = 8.0;
const ZOOM_STEP = 0.12;
const ZOOM_MIN = 1;

// ------------------------------------------------------------
// API PÚBLICA
// ------------------------------------------------------------

export async function abrirShack() {
  const modal = document.getElementById('modal-shack');
  if (!modal) return;

  modal.classList.add('modal--visible');
  mostrarSpinner(true);

  // Adaptar el shack al tema activo (puede cambiar entre aperturas)
  const esTemaClaro = document.body.classList.contains('tema-claro');
  if (_state.host) {
    _state.host.style.background = esTemaClaro ? '#dde1e7' : STAGE.bg;
  }
  if (_state.ambientLight) {
    _state.ambientLight.intensity = esTemaClaro ? 2.4 : 1.18;
  }
  if (_state.keyLight) {
    _state.keyLight.intensity = esTemaClaro ? 0.7 : 0.42;
  }

  try {
    if (!_state.listo) {
      _inicializar();
      // Aplicar tema tras inicializar (host y luces ya existen)
      if (_state.host) _state.host.style.background = esTemaClaro ? '#dde1e7' : STAGE.bg;
      if (_state.ambientLight) _state.ambientLight.intensity = esTemaClaro ? 2.4 : 1.18;
      if (_state.keyLight)     _state.keyLight.intensity     = esTemaClaro ? 0.7  : 0.42;
    } else if (_state._interactionHandlers) {
      window.addEventListener('pointermove', _state._interactionHandlers.onPointerMove);
      window.addEventListener('pointerup', _state._interactionHandlers.onPointerUp);
      window.addEventListener('keydown', _state._interactionHandlers.onKeyDown);
      if (!_state.resizeObserver && _state.host) {
        _state.resizeObserver = new ResizeObserver(() => {
          actualizarLayoutStage(_state, _actualizarDOMCalibrationUI);
        });
        _state.resizeObserver.observe(_state.host);
      }
    }

    if (!_state.animId) _iniciarLoop();

    actualizarLayoutStage(_state, _actualizarDOMCalibrationUI);
    centrarStageSiCorresponde(_state);
    resetearViewport(_state, ZOOM_MIN);

    const usuario = auth.currentUser;
    if (!usuario) {
      mostrarSpinner(false);
      return;
    }

    setUsuarioUid(usuario.uid);

    const { items } = await cargarEstadoShack(usuario.uid);
    _state.items = items;

    _ensureMapExists();
    _ordenarPorZIndex();

    await _renderizarItems(new Map());
    mostrarSpinner(false);

    const certItems = _state.items.filter((i) => i.tipo === 'certificado' && i.visible !== false);
    const sinEncuadrar = certItems.filter((i) => !i.encuadrado);

    if (sinEncuadrar.length > 0) {
      _mostrarAvisoPendientesEncuadrar(sinEncuadrar, certItems);
    } else if (certItems.length > 0) {
      const necesitaTokenDrive = certItems.some((i) => i.storageType !== 'managed_drive');
      if (necesitaTokenDrive && !obtenerAccessToken()) {
        _mostrarAvisoDriveNoAutorizado(certItems);
      } else {
        await _cargarThumbnailsYRenderizar(certItems);
      }
    } else {
      _state.thumbs = new Map();
    }

    if (!_state.readOnly) {
      _actualizarToolbarEstado();
      await _actualizarVisualCalibracion();
      _verificarCallsignPublicadoBackground(); // no await — habilita auto-preview si ya fue publicado
    }
  } catch (err) {
    console.error('[Shack3D]', err);
    mostrarToast(t('shack.error.cargar'), 'error');
    mostrarSpinner(false);
  }
}

export function cerrarShack() {
  document.getElementById('modal-shack')?.classList.remove('modal--visible');

  if (_state._interactionHandlers) {
    window.removeEventListener('pointermove', _state._interactionHandlers.onPointerMove);
    window.removeEventListener('pointerup', _state._interactionHandlers.onPointerUp);
    window.removeEventListener('keydown', _state._interactionHandlers.onKeyDown);
  }

  _state.resizeObserver?.disconnect();
  _state.resizeObserver = null;

  _state.drag.activo = false;
  _state.drag.tipo = null;
  _state.drag.itemKey = null;
  _state.drag.wall = null;
  _state.drag.cornerKey = null;

  _state.selection.itemKey = null;

  _ocultarGrid();
  _ocultarHelperRect();
  _actualizarToolbarEstado();

  const tt = document.getElementById('shack-tooltip-3d');
  if (tt) tt.classList.remove('shack-tooltip-3d--visible');

  const zoom = document.getElementById('shack-map-zoom');
  if (zoom) zoom.remove();

  if (_state.animId) {
    cancelAnimationFrame(_state.animId);
    _state.animId = null;
  }

  cancelarGuardadoDebounced();
  if (_previewTimer) { clearTimeout(_previewTimer); _previewTimer = null; }
  limpiarCacheThumbnails();
  _state.imagenesCache.clear();
}

// ------------------------------------------------------------
// INICIALIZACIÓN
// ------------------------------------------------------------

function _inicializar() {
  const THREE = T();
  const host = document.getElementById('shack-canvas-container');

  if (!THREE) {
    console.error('[Shack3D] window.THREE no está disponible');
    return;
  }

  if (!host) {
    console.error('[Shack3D] Falta #shack-canvas-container');
    return;
  }

  _state.host = host;
  _state.host.innerHTML = '';
  _state.host.style.position = 'relative';
  _state.host.style.overflow = 'hidden';
  _state.host.style.background = STAGE.bg;

  crearLayoutDOM(_state);
  if (!_state.readOnly) crearToolbar(_state, _handleToolbarAction);
  crearRenderer(_state, THREE);
  _crearBackplate();
  _crearEscenaPrincipal();

  _state.raycaster = new THREE.Raycaster();
  _state.mouse = new THREE.Vector2();

  _state.calibration = cargarCalibracion() || defaultCalibration();

  if (!_state.readOnly) {
    _state.pickWallMeshes = crearWallPickMeshes(_state.scene, _state.calibration);
    _state.gridObjects = crearGrids(_state.scene);

    rebuildGrid({ calibration: _state.calibration, gridObjects: _state.gridObjects, wall: 'trasera' });
    rebuildGrid({ calibration: _state.calibration, gridObjects: _state.gridObjects, wall: 'lateral' });

    _state.helperRect = crearHelperRect(_state.scene);
    _state.multiSelectRects = crearMultiSelectRects(_state.scene);

    crearDOMCalibrationUI({
      svg: _state.svg,
      overlay: _state.overlay,
      calibrationMode: () => _state.calibrationMode,
      drag: _state.drag,
      handleEls: _state.handleEls,
      outlineEls: _state.outlineEls,
    });
  }

  _crearItemsApi();

  if (!_state.readOnly) _actualizarDOMCalibrationUI();
  _configurarInteraccion();
  _configurarHotkeys();

  if (!_state.tooltipConfigurado) {
    configurarTooltip();
    _state.tooltipConfigurado = true;
  }

  _state.resizeObserver = new ResizeObserver(() => {
    actualizarLayoutStage(_state, _actualizarDOMCalibrationUI);
  });

  _state.resizeObserver.observe(_state.host);

  actualizarLayoutStage(_state, _actualizarDOMCalibrationUI);
  setListo(true);
}

function _crearItemsApi() {
  _itemsApi = crearItemsApi({
    state: _state,
    mostrarToast,
    obtenerAccessToken,
    solicitarPermisosDrive,
    generarThumbnailsEnLote,
    abrirEncuadrador,
    limpiarCacheFrameTextures,
    crearMapaGroup: _crearMapaGroup,
    crearCertificadoGroup: _crearCertificadoGroup,
    aplicarTransformItem: _aplicarTransformItem,
    guardarDebounced: _guardarDebounced,
    ordenarPorZIndex: _ordenarPorZIndex,
    nextZIndex: _nextZIndex,
    selectedItem: _selectedItem,
    mapUrl: MAPA_YAESU_URL,
  });
}

function _crearBackplate() {
  const THREE = T();

  _state.sceneBg = new THREE.Scene();
  _state.cameraBg = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);

  const bgMat = new THREE.MeshBasicMaterial({
    depthTest: false,
    depthWrite: false,
  });

  const bgMesh = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), bgMat);
  bgMesh.renderOrder = -1;
  _state.sceneBg.add(bgMesh);

  new THREE.TextureLoader().load(
    '/assets/shack-fondo2.png',
    (tex) => {
      tex.minFilter = THREE.LinearFilter;
      tex.magFilter = THREE.LinearFilter;
      bgMat.map = tex;
      bgMat.needsUpdate = true;
      _state.needsRender = true;
    },
    undefined,
    () => console.warn('[Shack3D] No se pudo cargar /assets/shack-fondo2.png')
  );
}

function _crearEscenaPrincipal() {
  const THREE = T();

  _state.scene = new THREE.Scene();

  _state.camera = new THREE.PerspectiveCamera(
    CAMARA.fov,
    IMAGE_RATIO,
    CAMARA.near,
    CAMARA.far
  );

  _state.camera.position.set(CAMARA.pos.x, CAMARA.pos.y, CAMARA.pos.z);
  _state.camera.lookAt(CAMARA.lookAt.x, CAMARA.lookAt.y, CAMARA.lookAt.z);
  _state.camera.setViewOffset(
    CAMARA.imgW,
    CAMARA.imgH,
    CAMARA.ppOffsetX,
    CAMARA.ppOffsetY,
    CAMARA.imgW,
    CAMARA.imgH
  );
  _state.camera.updateProjectionMatrix();

  const ambient = new THREE.AmbientLight(0xffffff, 1.18);
  _state.scene.add(ambient);
  _state.ambientLight = ambient;

  const key = new THREE.DirectionalLight(0xfff4e8, 0.42);
  key.position.set(-2.8, 3.4, 3.2);
  _state.scene.add(key);
  _state.keyLight = key;

  const fill = new THREE.DirectionalLight(0xffffff, 0.16);
  fill.position.set(3.0, 1.5, 2.0);
  _state.scene.add(fill);
}

// ------------------------------------------------------------
// TOOLBAR
// ------------------------------------------------------------

function _handleToolbarAction(action) {
  if (action.startsWith('distribute-')) {
    mostrarToast('Disponible próximamente.', 'ok');
    return;
  }
  switch (action) {
    case 'encuadrar':
      _encuadrarSeleccionado();
      break;
    case 'add-map':
      _agregarMapa();
      break;
    case 'toggle-wall':
      _toggleWallSelected();
      break;
    case 'delete':
      _deleteSelected();
      break;
    case 'align-left':
      _alignSelected('left');
      break;
    case 'align-center-h':
      _alignSelected('center-h');
      break;
    case 'align-right':
      _alignSelected('right');
      break;
    case 'align-top':
      _alignSelected('top');
      break;
    case 'align-middle':
      _alignSelected('middle');
      break;
    case 'align-bottom':
      _alignSelected('bottom');
      break;
    case 'size-minus':
      _ajustarTamano(-0.02);
      break;
    case 'size-plus':
      _ajustarTamano(0.02);
      break;
    case 'size-set':
      _aplicarTamanoInput();
      break;
    case 'distribute-h':
      _distributeSelected('h');
      break;
    case 'distribute-v':
      _distributeSelected('v');
      break;
    case 'front':
      _bringSelectedToFront();
      break;
    case 'back':
      _sendSelectedToBack();
      break;
    case 'toggle-calib':
      _state.calibrationMode = !_state.calibrationMode;
      _actualizarVisualCalibracion();
      mostrarToast(_state.calibrationMode ? 'Modo calibración activo.' : 'Modo calibración oculto.', 'ok');
      break;
    case 'save-calib':
      _imprimirYGuardarCalibracion();
      break;
    case 'zoom-reset':
      resetearViewport(_state, ZOOM_MIN);
      break;
    case 'gestionar-certs':
      _abrirPanelCertificados();
      break;
    case 'copy-iframe':
      _copiarIframeShack();
      break;
    default:
      break;
  }
}

async function _abrirPanelCertificados() {
  document.getElementById('shack-panel-certs')?.remove();

  const usuario = auth.currentUser;
  if (!usuario) return;

  const todosCerts = await cargarCertificadosDisponibles(usuario.uid, true);

  const panel = document.createElement('div');
  panel.id = 'shack-panel-certs';
  panel.style.cssText = [
    'position:absolute',
    'top:0','right:0',
    'width:280px',
    'max-height:100%',
    'overflow-y:auto',
    'background:rgba(10,10,14,0.97)',
    'border-left:1px solid rgba(255,255,255,0.10)',
    'z-index:90',
    'padding:14px 16px',
    'font:13px/1.4 system-ui,sans-serif',
    'color:#e0e0f0',
    'box-shadow:-8px 0 32px rgba(0,0,0,0.5)',
    'box-sizing:border-box',
  ].join(';');

  const header = document.createElement('div');
  header.style.cssText = 'display:flex;align-items:center;justify-content:space-between;margin-bottom:12px;';

  const titulo = document.createElement('strong');
  titulo.style.cssText = 'font-size:13px;letter-spacing:.01em;';
  titulo.textContent = t('shack.panel-certs.titulo');

  const btnCerrar = document.createElement('button');
  btnCerrar.textContent = '✕';
  btnCerrar.style.cssText = 'border:none;background:transparent;color:rgba(255,255,255,.5);font-size:15px;cursor:pointer;padding:2px 4px;line-height:1;';
  btnCerrar.addEventListener('click', () => panel.remove());

  header.append(titulo, btnCerrar);
  panel.appendChild(header);

  if (todosCerts.length === 0) {
    const vacio = document.createElement('p');
    vacio.style.cssText = 'color:rgba(255,255,255,0.40);font-size:12px;margin:0;';
    vacio.textContent = t('shack.panel-certs.vacio');
    panel.appendChild(vacio);
  }

  for (const cert of todosCerts) {
    const row = document.createElement('label');
    row.style.cssText = 'display:flex;align-items:flex-start;gap:10px;padding:9px 0;border-bottom:1px solid rgba(255,255,255,0.06);cursor:pointer;';

    const chk = document.createElement('input');
    chk.type = 'checkbox';
    chk.style.cssText = 'margin-top:2px;cursor:pointer;flex-shrink:0;';
    chk.checked = _state.items.some((i) => i.shackKey === cert.shackKey && i.visible !== false);

    const info = document.createElement('div');
    info.style.cssText = 'flex:1;min-width:0;';

    const nombre = document.createElement('div');
    nombre.style.cssText = 'font-size:12px;font-weight:600;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;';
    nombre.textContent = `${cert.concurso} ${cert.anio}`;

    const sub = document.createElement('div');
    sub.style.cssText = 'font-size:11px;color:rgba(255,255,255,0.50);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;';
    sub.textContent = cert.modo !== '—' ? cert.modo : cert.nombre;

    info.append(nombre, sub);
    row.append(chk, info);
    panel.appendChild(row);

    chk.addEventListener('change', async () => {
      const visible = chk.checked;
      chk.disabled = true;

      const ok = await actualizarShackVisibleArchivo(cert.edicionId, cert.archivoId, visible);
      if (!ok) {
        chk.checked = !visible;
        chk.disabled = false;
        mostrarToast(t('shack.panel-certs.error'), 'error');
        return;
      }

      if (!visible) {
        // Solo apagar visibilidad — conservar posición, marco y encuadre
        const item = _state.items.find((i) => i.shackKey === cert.shackKey);
        if (item) item.visible = false;
        await _renderizarItems(_state.thumbs ?? new Map());
      } else {
        const item = _state.items.find((i) => i.shackKey === cert.shackKey);
        if (item) {
          // Ya existe con su posición/marco: solo reencender
          item.visible = true;
        } else {
          // Primera vez que se agrega al layout
          const nuevoItem = {
            ...cert,
            wall: 'trasera',
            u: 0.5,
            v: 0.5,
            w: 0.30,
            h: 0.20,
            ratio: 1.5,
            frameTone: 0.60,
            zIndex: _nextZIndex(),
            visible: true,
          };
          _state.items.push(nuevoItem);
          const nuevosThumbs = await generarThumbnailsEnLote([nuevoItem]);
          if (!_state.thumbs) _state.thumbs = new Map();
          nuevosThumbs.forEach((url, key) => _state.thumbs.set(key, url));
        }
        await _renderizarItems(_state.thumbs ?? new Map());
      }

      _guardarDebounced();
      chk.disabled = false;
    });
  }

  _state.host.appendChild(panel);
}

function _actualizarToolbarEstado() {
  if (!_state.toolbar) return;

  const item = _selectedItem();
  const hasSel = !!item;
  const esCert = hasSel && item.tipo === 'certificado';

  _state.toolbar
    .querySelectorAll([
      '[data-act="toggle-wall"]',
      '[data-act="delete"]',
      '[data-act="front"]',
      '[data-act="back"]',
      '[data-act="size-minus"]',
      '[data-act="size-plus"]',
    ].join(', '))
    .forEach((btn) => {
      btn.disabled = !hasSel;
    });

  // Sincronizar input de tamaño con el item seleccionado.
  if (_state._sizeInput) {
    if (hasSel) {
      _state._sizeInput.value = Math.round(item.w * 100);
      _state._sizeInput.disabled = false;
    } else {
      _state._sizeInput.value = '';
      _state._sizeInput.disabled = true;
    }
  }

  // Alinear: habilitados solo con selección múltiple válida (2+ items, misma pared).
  const _multiKeys = new Set([_state.selection.itemKey, ...(_state.selection.selectedKeys ?? [])].filter(Boolean));
  const hasMultiSel = _multiKeys.size >= 2;
  _state.toolbar
    .querySelectorAll([
      '[data-act="align-left"]',
      '[data-act="align-center-h"]',
      '[data-act="align-right"]',
      '[data-act="align-top"]',
      '[data-act="align-middle"]',
      '[data-act="align-bottom"]',
    ].join(', '))
    .forEach((btn) => {
      btn.disabled = !hasMultiSel;
    });

  // Distribuir: siempre deshabilitado por ahora.
  _state.toolbar
    .querySelectorAll([
      '[data-act="distribute-h"]',
      '[data-act="distribute-v"]',
    ].join(', '))
    .forEach((btn) => {
      btn.disabled = true;
    });

  const esMapa = hasSel && item.tipo === 'mapa';
  const btnEncuadrar = _state.toolbar.querySelector('[data-act="encuadrar"]');
  if (btnEncuadrar) btnEncuadrar.disabled = !(esCert || esMapa);
}

// ------------------------------------------------------------
// LOOP
// ------------------------------------------------------------

function _iniciarLoop() {
  const loop = () => {
    _state.animId = requestAnimationFrame(loop);

    if (!_state.needsRender) return;
    _state.needsRender = false;

    if (!_state.renderer || !_state.sceneBg || !_state.cameraBg || !_state.scene || !_state.camera) return;

    _state.renderer.setScissorTest(false);
    _state.renderer.clear();

    _state.renderer.setViewport(0, 0, _state.stageSize.w, _state.stageSize.h);

    _state.renderer.setViewport(
      _state.imageView.x,
      _state.stageSize.h - _state.imageView.y - _state.imageView.h,
      _state.imageView.w,
      _state.imageView.h
    );
    _state.renderer.setScissor(
      _state.imageView.x,
      _state.stageSize.h - _state.imageView.y - _state.imageView.h,
      _state.imageView.w,
      _state.imageView.h
    );
    _state.renderer.setScissorTest(true);

    _state.renderer.render(_state.sceneBg, _state.cameraBg);
    _state.renderer.render(_state.scene, _state.camera);

    _state.renderer.setScissorTest(false);
  };

  loop();
}

// ------------------------------------------------------------
// ITEMS
// ------------------------------------------------------------

function _ensureMapExists() {
  return _itemsApi?.ensureMapExists();
}

async function _cargarThumbnailsYRenderizar(certItems) {
  return _itemsApi?.cargarThumbnailsYRenderizar(certItems);
}

function _mostrarAvisoPendientesEncuadrar(sinEncuadrar, todosLosCerts) {
  return _itemsApi?.mostrarAvisoPendientesEncuadrar(sinEncuadrar, todosLosCerts);
}

function _mostrarAvisoDriveNoAutorizado(certItems) {
  return _itemsApi?.mostrarAvisoDriveNoAutorizado(certItems);
}

async function _renderizarItems(thumbs) {
  return _itemsApi?.renderizarItems(thumbs);
}

async function _encuadrarSeleccionado() {
  return _itemsApi?.encuadrarSeleccionado();
}

async function _agregarMapa() {
  return _itemsApi?.agregarMapa();
}

async function _toggleWallSelected() {
  return _itemsApi?.toggleWallSelected();
}

async function _deleteSelected() {
  const out = await _itemsApi?.deleteSelected();
  _actualizarToolbarEstado();
  return out;
}

async function _alignSelected(mode) {
  await _itemsApi?.alignSelected(mode);
  const sel = _selectedItem();
  if (sel) _actualizarHelperRect(sel);
  _actualizarMultiSelectRects();
}

async function _ajustarTamano(delta) {
  const item = _selectedItem();
  if (!item || _state.readOnly) return;

  const keys = new Set([_state.selection.itemKey, ...(_state.selection.selectedKeys ?? [])].filter(Boolean));
  for (const key of keys) {
    const it = key === item.shackKey ? item : _itemByKey(key);
    if (!it) continue;
    it.w = clampNum(it.w + delta, MIN_W, MAX_W);
    if (it.frameRatio > 0) it.h = it.w / it.frameRatio;
    else if (it.ratio > 0) it.h = it.w / it.ratio;
  }

  if (_state._sizeInput) _state._sizeInput.value = Math.round(item.w * 100);
  await _renderizarItems(_state.thumbs);
  _actualizarHelperRect(item);
  _actualizarMultiSelectRects();
  _guardarDebounced();
}

async function _aplicarTamanoInput() {
  const item = _selectedItem();
  if (!item || !_state._sizeInput || _state.readOnly) return;

  const rawPct = parseFloat(_state._sizeInput.value);
  if (isNaN(rawPct) || rawPct <= 0) {
    _state._sizeInput.value = Math.round(item.w * 100);
    return;
  }

  const nuevoW = clampNum(rawPct / 100, MIN_W, MAX_W);
  const factor = item.w > 0 ? nuevoW / item.w : 1;

  const keys = new Set([_state.selection.itemKey, ...(_state.selection.selectedKeys ?? [])].filter(Boolean));
  for (const key of keys) {
    const it = key === item.shackKey ? item : _itemByKey(key);
    if (!it) continue;
    it.w = key === item.shackKey ? nuevoW : clampNum(it.w * factor, MIN_W, MAX_W);
    if (it.frameRatio > 0) it.h = it.w / it.frameRatio;
    else if (it.ratio > 0) it.h = it.w / it.ratio;
  }

  _state._sizeInput.value = Math.round(item.w * 100);
  await _renderizarItems(_state.thumbs);
  _actualizarHelperRect(item);
  _actualizarMultiSelectRects();
  _guardarDebounced();
}

async function _distributeSelected(axis) {
  return _itemsApi?.distributeSelected(axis);
}

function _bringSelectedToFront() {
  _itemsApi?.bringSelectedToFront();
  _ordenarByAndRender();
}

function _sendSelectedToBack() {
  _itemsApi?.sendSelectedToBack();
  _ordenarByAndRender();
}

async function _ordenarByAndRender() {
  return _itemsApi?.ordenarByAndRender();
}

function _aplicarTransformItem(group, item) {
  if (!group || !item) return;

  group.position.set(0, 0, 0);
  group.rotation.set(0, 0, 0);
  group.quaternion.identity();
  group.scale.set(1, 1, 1);

  group.children.forEach((child) => {
    if (child?.userData?.surfaceWarp) {
      updateWarpedMeshGeometry(
        child,
        _state.calibration,
        item.wall,
        item.u,
        item.v,
        item.w,
        item.h
      );
      return;
    }

    if (child?.userData?.surfaceNail) {
      const nail = child.userData.surfaceNail;
      const point = surfaceRectPoint(
        _state.calibration,
        item.wall,
        item.u,
        item.v,
        item.w,
        item.h,
        nail.x || 0,
        nail.y || 0,
        nail.zOffset || 0
      );
      child.position.copy(point);
      child.rotation.set(0, 0, 0);
      child.quaternion.identity();
      child.scale.set(1, 1, 1);
      child.updateMatrixWorld(true);
    }
  });

  if (_state.helperRect?.visible && _state.selection.itemKey === item.shackKey) {
    _actualizarHelperRect(item);
  }

  _state.needsRender = true;
}

// ------------------------------------------------------------
// SUPERFICIE BILINEAL
// ------------------------------------------------------------

function _surfaceFrameAt(wall, u, v, w, h) {
  return surfaceFrameAt(_state.calibration, wall, u, v, w, h);
}

function _surfacePoint(wall, u, v) {
  return surfacePoint(_state.calibration, wall, u, v);
}

// ------------------------------------------------------------
// CALIBRACIÓN
// ------------------------------------------------------------

function _actualizarDOMCalibrationUI() {
  actualizarDOMCalibrationUI({
    svg: _state.svg,
    calibration: _state.calibration,
    calibrationMode: _state.calibrationMode,
    handleEls: _state.handleEls,
    outlineEls: _state.outlineEls,
    camera: _state.camera,
    imageView: _state.imageView,
  });
}

async function _actualizarVisualCalibracion(opciones = {}) {
  await actualizarVisualCalibracion({
    calibration: _state.calibration,
    gridObjects: _state.gridObjects,
    pickWallMeshes: _state.pickWallMeshes,
    calibrationMode: _state.calibrationMode,
    actualizarDOM: _actualizarDOMCalibrationUI,
    actualizarLayoutStage: () => actualizarLayoutStage(_state, _actualizarDOMCalibrationUI),
    centrarStageSiCorresponde: () => centrarStageSiCorresponde(_state),
    renderizarItems: _renderizarItems,
    thumbs: _state.thumbs,
    relayout: opciones.relayout ?? true,
    recenter: opciones.recenter ?? false,
    rerenderItems: opciones.rerenderItems ?? false,
  });
  _state.needsRender = true;
}

function _actualizarVisualCalibracionLigera() {
  actualizarVisualCalibracion({
    calibration: _state.calibration,
    gridObjects: _state.gridObjects,
    pickWallMeshes: _state.pickWallMeshes,
    calibrationMode: _state.calibrationMode,
    actualizarDOM: _actualizarDOMCalibrationUI,
    actualizarLayoutStage: () => actualizarLayoutStage(_state, _actualizarDOMCalibrationUI),
    centrarStageSiCorresponde: () => centrarStageSiCorresponde(_state),
    renderizarItems: _renderizarItems,
    thumbs: _state.thumbs,
    relayout: false,
    recenter: false,
    rerenderItems: false,
  });
  _state.needsRender = true;
}

function _mostrarGrid(wall) {
  mostrarGrid(_state.gridObjects, _state.calibrationMode, wall);
}

function _ocultarGrid() {
  ocultarGrid(_state.gridObjects, _state.calibrationMode);
}

function _actualizarHelperRect(item) {
  actualizarHelperRect({
    helperRect: _state.helperRect,
    calibration: _state.calibration,
    item,
  });
}

function _ocultarHelperRect() {
  ocultarHelperRect(_state.helperRect);
}

function _actualizarMultiSelectRects() {
  if (!_state.multiSelectRects?.length) return;
  actualizarMultiSelectRects({
    rects: _state.multiSelectRects,
    calibration: _state.calibration,
    items: _state.items,
    selectedKeys: _state.selection.selectedKeys,
    primaryKey: _state.selection.itemKey,
  });
  _state.needsRender = true;
}

function _guardarCalibracionLocal() {
  guardarCalibracionLocal(_state.calibration);
}

function _imprimirYGuardarCalibracion() {
  imprimirYGuardarCalibracion(_state.calibration, mostrarToast);
}

// ------------------------------------------------------------
// INTERACCIÓN
// ------------------------------------------------------------

function _configurarInteraccion() {
  const handlers = crearHandlersInteraccion({
    state: _state,
    modalVisible: _state.readOnly
      ? () => true
      : () => document.getElementById('modal-shack')?.classList.contains('modal--visible'),
    selectedItem: _selectedItem,
    itemByKey: _itemByKey,
    meshByItemKey: _meshByItemKey,
    mostrarGrid: _mostrarGrid,
    ocultarGrid: _ocultarGrid,
    actualizarHelperRect: _actualizarHelperRect,
    guardarCalibracionLocal: _guardarCalibracionLocal,
    actualizarVisualCalibracion: _actualizarVisualCalibracion,
    actualizarVisualCalibracionLigera: _actualizarVisualCalibracionLigera,
    guardarDebounced: _guardarDebounced,
    renderizarItems: _renderizarItems,
    aplicarViewport: () => aplicarViewport(_state),
    aplicarCameraViewOffset: () => aplicarCameraViewOffset(_state),
    resetearViewport: () => resetearViewport(_state, ZOOM_MIN),
    abrirMapaZoom: _abrirMapaZoom,
    abrirItemFullscreen: _abrirItemFullscreen,
    deleteSelected: _deleteSelected,
    actualizarToolbarEstado: _actualizarToolbarEstado,
    toggleCalib: () => _handleToolbarAction('toggle-calib'),
    actualizarMultiSelectRects: _actualizarMultiSelectRects,
    mostrarToast,
    aplicarTransformItem: _aplicarTransformItem,
    round4: _round4,
    surfacePoint: _surfacePoint,
    mapUrlFallback: MAPA_YAESU_URL,
    minTone: MIN_TONE,
    maxTone: MAX_TONE,
    minW: MIN_W,
    maxW: MAX_W,
    zoomMin: ZOOM_MIN,
    zoomMax: ZOOM_MAX,
    zoomStep: ZOOM_STEP,
    readOnly: _state.readOnly,
  });

  _state._interactionHandlers = handlers;

  configurarInteraccion({
    canvas: _state.canvas,
    onPointerDown: handlers.onPointerDown,
    onPointerMove: handlers.onPointerMove,
    onPointerUp: handlers.onPointerUp,
    onWheel: handlers.onWheel,
    onDoubleClick: handlers.onDoubleClick,
  });
}

function _configurarHotkeys() {
  if (!_state._interactionHandlers) return;
  configurarHotkeys(_state._interactionHandlers.onKeyDown);
}

// ------------------------------------------------------------
// MAPA ZOOM
// ------------------------------------------------------------

function _abrirMapaZoom(src) {
  abrirMapaZoom(_clampNum, src);
}

function _abrirItemFullscreen(src, titulo) {
  abrirItemFullscreen(src, titulo);
}

// ------------------------------------------------------------
// COMPARTIR
// ------------------------------------------------------------

function _obtenerCallsign() {
  const desdePerfil = getPerfilActual()?.indicativoOperador?.trim().toUpperCase();
  if (desdePerfil) return desdePerfil;
  // Fallback al DOM para el caso improbable de que el perfil no esté cargado aún
  return document.getElementById('navbar-indicativo')?.textContent?.trim().toUpperCase() || null;
}

async function _copiarIframeShack() {
  const callsign = _obtenerCallsign();
  if (!callsign) {
    mostrarToast(t('shack.toast.iframe-error-callsign'), 'error');
    return;
  }

  const uid = _state.usuarioUid;
  if (!uid) {
    mostrarToast(t('shack.toast.iframe-error-publicar'), 'error');
    return;
  }

  const confirmado = await mostrarConfirmacion(
    t('shack.confirma.publicar.titulo'),
    t('shack.confirma.publicar.subtitulo'),
    { icono: '🔗', labelConfirmar: t('shack.confirma.publicar.btn') }
  );
  if (!confirmado) return;

  let publicUrls;
  try {
    publicUrls = await subirImagenesPublicasShack(uid, _state.items, _state.thumbs);
  } catch (err) {
    console.error('[Shack] Error al subir imágenes públicas:', err);
    mostrarToast(t('shack.toast.iframe-error-publicar'), 'error');
    return;
  }

  try {
    await guardarShackPublico(callsign, uid, _state.items, publicUrls, _state.calibration);
  } catch (err) {
    console.error('[Shack] Error al guardar shack público:', err);
    mostrarToast(t('shack.toast.iframe-error-publicar'), 'error');
    return;
  }

  // Marcar shack como publicado — activa auto-preview en ediciones futuras
  _callsignPublicado = callsign;

  // Generar y subir la imagen de preview
  let previewUrl = null;
  let version = Date.now();
  try {
    const blob = await _capturarPreviewBlob();
    if (blob) {
      const result = await actualizarPreviewPublica(callsign, uid, blob);
      previewUrl = result.previewUrl;
      version = result.previewVersion;
      _previewVersion = version;
    }
  } catch (err) {
    console.warn('[Shack] Error al generar preview:', err);
  }

  // Limpiar imágenes públicas huérfanas — no bloquea ni afecta la publicación
  try {
    const keysActivos = _state.items
      .filter((i) => i.tipo === 'certificado' && i.visible !== false && i.fileId)
      .map((i) => i.shackKey);
    const eliminados = await limpiarImagenesHuerfanas(uid, keysActivos);
    if (eliminados > 0) console.log(`[Shack] Huérfanos eliminados: ${eliminados}`);
  } catch (err) {
    console.warn('[Shack] Error al limpiar huérfanos (no crítico):', err);
  }

  // Copiar tarjeta HTML estática para QRZ (solo inline styles, sin JS ni CSS externo)
  const shackUrl = `https://app.contestmanager.uy/shack/${callsign}`;
  const imgSrc   = previewUrl ? `${previewUrl}&v=${version}` : '';
  const labelVirtualDe = t('shack.qrz.virtual-de');
  const labelCta       = t('shack.qrz.cta');

  const embedHtml = previewUrl
    ? `<a href="${shackUrl}" target="_blank" style="text-decoration:none;display:block;font-family:Arial,sans-serif;color:#e6edf3;"><div style="background:#161b22;border:1px solid #30363d;border-radius:14px;overflow:hidden;box-shadow:0 8px 32px rgba(0,212,170,0.08);"><div style="height:2px;background:linear-gradient(90deg,transparent,#00d4aa 30%,#f0a500 70%,transparent);"></div><div style="padding:14px 20px 10px;text-align:center;"><div style="font-size:1.20rem;font-weight:700;line-height:1.2;margin:0 0 3px;color:#e6edf3;letter-spacing:0.01em;">Contest <span style="color:#00d4aa;">Manager</span></div><div style="font-size:0.78rem;font-weight:400;color:#8b949e;margin:0;">${labelVirtualDe} <span style="color:#e6edf3;font-weight:600;letter-spacing:0.03em;">${callsign}</span></div></div><img src="${imgSrc}" style="width:100%;height:auto;max-width:1920px;display:block;border:0;margin:0 auto;" alt="Shack Virtual ${callsign}"><div style="padding:7px 16px 9px;text-align:center;border-top:1px solid #21262d;"><span style="font-size:0.67rem;color:rgba(255,255,255,0.28);letter-spacing:0.01em;line-height:1.5;">${labelCta}</span></div></div></a>`
    : `<iframe src="${shackUrl}" width="100%" height="650" frameborder="0" loading="lazy"></iframe>`;

  navigator.clipboard.writeText(embedHtml)
    .then(() => mostrarToast(t('shack.toast.iframe-copiado'), 'ok'))
    .catch(() => mostrarToast(t('shack.toast.iframe-error-clipboard'), 'error'));
}

// ------------------------------------------------------------
// PERSISTENCIA
// ------------------------------------------------------------

function _guardarDebounced() {
  if (!_state.usuarioUid) return;

  guardarDebouncedState(async () => {
    await guardarEstadoShack(_state.usuarioUid, _state.items);
  }, 260);

  _actualizarPreviewDebounced();
}

function _capturarPreviewBlob() {
  return new Promise((resolve) => {
    const renderer = _state.renderer;
    if (!renderer) { resolve(null); return; }
    const iv = _state.imageView;
    const stageH = _state.stageSize?.h ?? 0;
    if (!iv || iv.w < 1 || iv.h < 1 || stageH < 1) { resolve(null); return; }

    // Render sincrónico antes de leer el canvas — garantiza contenido fresco
    // independientemente del estado del flag needsRender en el loop RAF.
    if (_state.sceneBg && _state.cameraBg && _state.scene && _state.camera) {
      renderer.setScissorTest(false);
      renderer.clear();
      renderer.setViewport(iv.x, stageH - iv.y - iv.h, iv.w, iv.h);
      renderer.setScissor(iv.x, stageH - iv.y - iv.h, iv.w, iv.h);
      renderer.setScissorTest(true);
      renderer.render(_state.sceneBg, _state.cameraBg);
      renderer.render(_state.scene, _state.camera);
      renderer.setScissorTest(false);
    }

    const src = renderer.domElement;
    const pr = renderer.getPixelRatio();
    const out = document.createElement('canvas');
    out.width = PREVIEW_EXPORT.w;
    out.height = PREVIEW_EXPORT.h;
    const ctx = out.getContext('2d');
    ctx.drawImage(src, iv.x * pr, iv.y * pr, iv.w * pr, iv.h * pr, 0, 0, PREVIEW_EXPORT.w, PREVIEW_EXPORT.h);
    out.toBlob(resolve, 'image/png');
  });
}

function _actualizarPreviewDebounced() {
  if (!_state.usuarioUid || !_callsignPublicado) return;
  if (_previewTimer) clearTimeout(_previewTimer);
  _previewTimer = setTimeout(async () => {
    _previewTimer = null;
    const uid = _state.usuarioUid;
    const callsign = _callsignPublicado;
    if (!uid || !callsign) return;
    try {
      // Abortar si los thumbnails aún no están todos cargados para evitar
      // escribir fileUrl:'' en publicShacks con merge:false (corrompería el doc).
      const certsVisibles = _state.items.filter(
        (i) => i.tipo === 'certificado' && i.visible !== false && i.fileId
      );
      const thumbsOk = certsVisibles.every((i) => _state.thumbs?.has(i.fileId));
      if (certsVisibles.length > 0 && !thumbsOk) {
        console.warn('[Shack] Auto-sync abortado: thumbnails incompletos.');
        return;
      }
      // 1. Subir imágenes de certificados (idempotente — sobreescribe misma ruta)
      const publicUrls = await subirImagenesPublicasShack(uid, _state.items, _state.thumbs);
      // 2. Actualizar shackItems + calibration en publicShacks (sync completo)
      await guardarShackPublico(callsign, uid, _state.items, publicUrls, _state.calibration);
      // 3. Generar y subir preview estática; merge previewUrl/previewVersion
      const blob = await _capturarPreviewBlob();
      if (!blob) return;
      const result = await actualizarPreviewPublica(callsign, uid, blob);
      _previewVersion = result.previewVersion;
    } catch (err) {
      console.warn('[Shack3D] Auto-sync public failed:', err);
    }
  }, 2500);
}

async function _verificarCallsignPublicadoBackground() {
  const callsign = _obtenerCallsign();
  if (!callsign || _callsignPublicado) return;
  try {
    const snap = await verificarShackPublicado(callsign);
    if (snap) {
      _callsignPublicado = callsign;
      _previewVersion = snap.previewVersion || null;
    }
  } catch {
    // Silencioso — la preview automática es opcional
  }
}

// ------------------------------------------------------------
// FRAMES / MAPAS
// ------------------------------------------------------------

function _crearCertificadoGroup(item, baseImage) {
  return crearCertificadoGroupMod({
    item,
    baseImage,
    frameModelTextureCache: _state.frameModelTextureCache,
    renderer: _state.renderer,
    pickables: _state.pickables,
    aplicarTransformItem: _aplicarTransformItem,
    onFrameTextureUpdate: () => { _state.needsRender = true; },
  });
}

function _crearMapaGroup(item, baseImage) {
  return crearMapaGroupMod({
    item,
    baseImage,
    frameModelTextureCache: _state.frameModelTextureCache,
    renderer: _state.renderer,
    pickables: _state.pickables,
    aplicarTransformItem: _aplicarTransformItem,
    onFrameTextureUpdate: () => { _state.needsRender = true; },
  });
}

// ------------------------------------------------------------
// HELPERS DE ESTADO
// ------------------------------------------------------------

function _nextZIndex() {
  return nextZIndexState();
}

function _ordenarPorZIndex() {
  ordenarPorZIndexState();
}

// ------------------------------------------------------------
// HELPERS VARIOS
// ------------------------------------------------------------

function _selectedItem() {
  return selectedItemState();
}

function _itemByKey(key) {
  return itemByKeyState(key);
}

function _meshByItemKey(key) {
  return meshByItemKeyState(key);
}

function _round4(n) {
  return round4(n);
}

function _clampNum(v, min, max) {
  return clampNum(v, min, max);
}

// ------------------------------------------------------------
// MODO READ-ONLY
// ------------------------------------------------------------

export function setShackReadOnly(value) {
  _state.readOnly = Boolean(value);
  _state.listo = false;
}

// ------------------------------------------------------------
// VISTA PÚBLICA (sin auth, sin Drive)
// ------------------------------------------------------------

export async function abrirShackPublico(uid, itemsPublicos = null, calibrationPublica = null) {
  _state.readOnly = true;
  _state.listo = false;

  mostrarSpinner(true);

  try {
    _inicializar();

    // En modo público, nunca usar localStorage del visitante.
    // Si el dueño guardó su calibración en Firestore, usarla; si no, defaults.
    _state.calibration = calibrationPublica || defaultCalibration();

    if (!_state.animId) _iniciarLoop();

    actualizarLayoutStage(_state, _actualizarDOMCalibrationUI);
    centrarStageSiCorresponde(_state);
    resetearViewport(_state, ZOOM_MIN);

    if (itemsPublicos !== null) {
      _state.items = itemsPublicos;
    } else {
      const shack = await obtenerShack(uid);
      _state.items = Array.isArray(shack.items) ? shack.items : [];
    }
    _ordenarPorZIndex();

    // Pre-carga imágenes con CORS en imagenesCache antes de renderizar.
    // renderizarItems usa state.imagenesCache como fallback cuando thumbs está vacío.
    // Sin crossOrigin='anonymous' Three.js rechaza la imagen al usarla como textura de canvas.
    await Promise.all(
      _state.items
        .filter((i) => i.tipo === 'certificado' && i.fileUrl)
        .map(
          (item) =>
            new Promise((resolve) => {
              const img = new Image();
              img.crossOrigin = 'anonymous';
              img.onload = () => {
                _state.imagenesCache.set(item.fileId, img);
                resolve();
              };
              img.onerror = () => resolve();
              img.src = item.fileUrl;
            })
        )
    );

    await _renderizarItems(new Map());
    mostrarSpinner(false);
  } catch (err) {
    console.error('[Shack3D] abrirShackPublico:', err);
    mostrarSpinner(false);
  }
}